import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  TextField,
  FormLayout,
  Toast,
  Loading,
  Checkbox,
  Box,
  Thumbnail,
  DropZone,
  InlineError,
  Text,
  Button,
  Modal,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen copy";

const AddEditSubcategory = ({ mode }) => {
  const navigate = useNavigate();
  const { id, subcategoryId } = useParams();

  const [isSaving, setIsSaving] = useState(mode === "edit");
  const [active, setActive] = useState(false);
  const toggleActive = () => setActive((a) => !a);

  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [item, setItem] = useState({
    name: { en: "", ar: "" },
    is_active: true,
    image: "", // preview URL or existing image_url
  });

  const [errors, setErrors] = useState({
    name: { en: "", ar: "" },
  });

  const [fileError, setFileError] = useState("");
  const [isImageUploading, setIsImageUploading] = useState(false);

  // ✅ S3 key like your CaseStudy
  const imageKey = useRef("");

  const handleChange = useCallback((field, lang, value) => {
    setItem((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: "",
      },
    }));
  }, []);

  // ---------- S3 UPLOAD (same pattern) ----------
  const sendFile = (file) => {
    setIsImageUploading(true);
    setFileError("");
    console.log(file);
    const formData = new FormData();
    formData.append("entity", "subcategories"); // 🔁 change if your backend expects something else
    formData.append("image", file);

    axios
      .post("/file/s3/save", formData)
      .then((res) => {
        imageKey.current = res.data.data; // ✅ store s3 file_key
        setItem((p) => ({
          ...p,
          image: URL.createObjectURL(file), // ✅ preview only
        }));
        setIsImageUploading(false);
      })
      .catch(() => {
        // setFileError("Upload failed, please try again");
      })
      .finally(() => console.log(""));
  };

  const handleDrop = useCallback((_dropFiles, acceptedFiles, rejectedFiles) => {
    if (rejectedFiles?.length) {
      setFileError("Unsupported file type or size too large");
      return;
    }

    const file = acceptedFiles?.[0];
    if (!file) return;

    sendFile(file);
  }, []);

  // ----------- VALIDATION -----------
  const validateFields = () => {
    let hasError = false;
    const newErrors = { name: { en: "", ar: "" } };

    ["en", "ar"].forEach((lang) => {
      if (!item.name[lang]?.trim()) {
        hasError = true;
        newErrors.name[lang] = "This field is required";
      }
    });

    // require image: either existing image url (edit) OR newly uploaded key
    const hasImage = !!item.image || !!imageKey.current;
    if (!hasImage) {
      hasError = true;
      setFileError("Image is required");
    } else {
      setFileError("");
    }

    setErrors(newErrors);
    return !hasError;
  };

  // ---------- LOAD EDIT ----------
  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/categories/${id}/sub-categories/${subcategoryId}`)
        .then((res) => {
          const d = res?.data;

          setItem({
            name: {
              en: d?.name?.en || "",
              ar: d?.name?.ar || "",
            },
            is_active: !!d?.is_active,
            image: d?.icon || "",
          });

          // IMPORTANT: don't set imageKey here (we only set it when uploading new file)
          imageKey.current = "";
          setIsSaving(false);
        })
        .catch(console.error);
    }
  }, [mode, id]);

  // ---------- SAVE ----------
  function handleSave() {
    if (!validateFields()) return;

    setIsSaving(true);

    const formData = new FormData();
    if (mode === "edit") formData.append("_method", "PATCH");

    formData.append("name[en]", item.name.en);
    formData.append("name[ar]", item.name.ar);
    formData.append("is_active", item.is_active ? 1 : 0);
    console.log("imageKey.current=", imageKey.current);

    // ✅ send S3 key instead of raw file
    if (imageKey.current) {
      // if (imageKey.current === "remove") {
      //   formData.append("icon", "remove.png");
      // } else {
      formData.append("icon", imageKey.current);
      // }
    }

    axios
      .post(
        mode === "new"
          ? `/categories/${id}/sub-categories`
          : `/categories/${id}/sub-categories/${subcategoryId}`,
        formData
      )
      .then(() => {
        mode === "new"
          ? navigate(`/admin/categories/${id}/subcategories`)
          : toggleActive();
      })
      .catch(console.error)
      .finally(() => setIsSaving(false));
  }
  const [activeDelete, setActiveDelete] = useState(false);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/categories/${id}`)
      .then((result) => {
        navigate("/admin/categories");
      })
      .catch((err) => console.log(err));
  };
  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Subcategory`}
      narrowWidth
      backAction={{
        onAction: () => navigate(`/admin/categories/${id}/subcategories`),
      }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <FormLayout.Group>
            <TextField
              label="Name (English)"
              value={item.name.en}
              onChange={(v) => handleChange("name", "en", v)}
              error={errors.name.en}
            />
            <TextField
              label="Name (Arabic)"
              value={item.name.ar}
              onChange={(v) => handleChange("name", "ar", v)}
              error={errors.name.ar}
              align="right"
            />
          </FormLayout.Group>

          <Text>Image</Text>
          <div style={{ display: "flex" }}>
            <Thumbnail size="large" source={item.image || ""} />
            <div style={{ width: "100%", marginLeft: 10 }}>
              <DropZone
                allowMultiple={false}
                onDrop={handleDrop}
                accept=".png,.jpg,.jpeg,.webp"
              >
                <DropZone.FileUpload actionHint="Accepts PNG, JPEG, WEBP" />
              </DropZone>
            </div>
          </div>

          {fileError && <InlineError message={fileError} />}

          <Checkbox
            label="Is active"
            checked={item.is_active}
            onChange={(v) => setItem((prev) => ({ ...prev, is_active: v }))}
          />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      {toastMarkup}

      {isImageUploading && <LoadingScreen />}
      <Modal
        open={activeDelete}
        onClose={() => setActiveDelete(false)}
        title="Delete Item"
        // primaryAction={{
        //   content: "Confirm Delete",
        //   destructive: true,
        //   onAction: handleYesDelete,
        //   loading: isSaving,
        // }}
        // secondaryActions={[
        //   { content: "Cancel", onAction: () => setActiveDelete(false) },
        // ]}

        primaryAction={{
          content: "Yes",
          destructive: true,
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            loading: isSaving,
            onAction: () => setActiveDelete(false),
          },
        ]}
      >
        <Modal.Section>
          <Text>
            <p>Are you sure you want to delete this item?</p>
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default AddEditSubcategory;
